/**
 * Video Store - Zustand
 *
 * Runtime state for the motion timeline: a mirror of `Project.video`, the
 * playhead/clock, and a timeline-only undo stack that is deliberately
 * separate from canvas undo.
 *
 * Persistence: every mutation writes the new VideoProject onto
 * `useProjectStore.currentProject.video` (which autosave already persists)
 * and marks the canvas store dirty. Scenes are always re-synced to the
 * project's current `pages[]` on write, so scenes and pages cannot drift.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  AudioTrack,
  Project,
  ProjectPage,
  SceneCue,
  SceneTransition,
  TimelineState,
  VideoExportSettings,
  VideoProject,
} from '@canvas/shared-types';
import { useProjectStore } from '@/stores/projectStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { applyMotionTemplate, motionTemplateById } from '@/data/motionTemplates';
import { clampAudioTrack } from '@/lib/video/audioTrack';
import { createPlaybackClock, type PlaybackClock } from '@/lib/video/playbackClock';
import { createClipProject, migrateVideoProject } from '@/lib/video/timelineEdit';
import {
  buildCues,
  createVideoProject,
  setSceneDuration as setSceneDurationPure,
  setSceneThumbAt,
  setTransition as setTransitionPure,
  syncScenesToPages,
} from '@/lib/video/sceneTimeline';
import { explainDurationClamp } from '@/lib/video/videoDocLimits';

/** Timeline undo depth. VideoProject is small JSON, so this is cheap. */
const TIMELINE_HISTORY_LIMIT = 20;

export const MIN_PX_PER_SEC = 20;
export const MAX_PX_PER_SEC = 240;
const DEFAULT_PX_PER_SEC = 80;

interface MutateOptions {
  /** Push the pre-change state onto the undo stack (default true). */
  record?: boolean;
}

export interface VideoStore extends TimelineState {
  /** Project the mirror belongs to. */
  projectId: string | null;
  /** Mirror of `currentProject.video`. */
  video: VideoProject | null;
  past: VideoProject[];
  future: VideoProject[];
  /** Transient user-facing message (e.g. why a scene could not be added). */
  notice: string | null;

  setNotice: (notice: string | null) => void;
  /** Turn motion mode on, creating the timeline from pages on first use. */
  enableMotion: () => void;
  disableMotion: () => void;
  /** Heal scenes against pages. No-op (and no write) when already in sync. */
  syncToPages: (pages: ProjectPage[]) => void;
  setSceneDuration: (pageId: string, ms: number, opts?: MutateOptions) => void;
  /** Capture the strip poster at a scene-local time. */
  setSceneThumb: (pageId: string, atMs: number) => void;
  /** Resize the export and scene lengths to a reel-platform template. */
  applyMotionTemplate: (templateId: string) => void;
  setTransition: (pageId: string, transition: SceneTransition, opts?: MutateOptions) => void;
  /** Push the current state onto the undo stack (call at gesture start). */
  checkpoint: () => void;
  undo: () => void;
  redo: () => void;

  /**
   * Scene being previewed on the live canvas. Playback is scoped to the
   * active scene; cross-scene "play all" arrives with the export compositor.
   */
  previewCue: SceneCue | null;
  /** Scene-local time the live canvas should show, or null for no preview. */
  previewLocalMs: number | null;

  /** Play the active scene, from the playhead if it is inside that scene. */
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  /** End preview and return the playhead to the previewed scene's start. */
  stop: () => void;
  /** Scrub: inside the active scene this previews that frame. */
  seek: (ms: number) => void;
  /** Move the playhead only — never enters preview. */
  cueTo: (ms: number) => void;
  setLoop: (loop: boolean) => void;
  setPxPerSec: (pxPerSec: number) => void;
  selectScene: (pageId: string | null) => void;

  /** Replace (or remove, with null) the timeline's music track. */
  setAudio: (track: AudioTrack | null) => void;
  /** Patch the music track; values are clamped to legal ranges. */
  updateAudio: (patch: Partial<AudioTrack>, opts?: MutateOptions) => void;
  /** Preview-only mute; never affects the export. */
  setMuted: (muted: boolean) => void;
  /** Selected timeline clip. Not a document property. */
  selectedClipId: string | null;
  selectClip: (id: string | null) => void;
  /** Immutable edit of a v2 timeline (or a v1 no-op if the helper bails). */
  changeTimeline: (fn: (video: VideoProject) => VideoProject, opts?: MutateOptions) => void;

  /** Remember the last-used export settings on the document (not undoable). */
  setExportSettings: (settings: VideoExportSettings) => void;
  /** The one cancel handle for a running export; null when idle. */
  exportController: AbortController | null;
  setExportController: (controller: AbortController | null) => void;
}

let clock: PlaybackClock | null = null;

/** The clock runs in SCENE-LOCAL time over `previewCue`. */
function getClock(): PlaybackClock {
  if (!clock) {
    const state = useVideoStore.getState();
    clock = createPlaybackClock({
      durationMs: 0,
      loop: state.loop,
      onTick: (local) => {
        const state = useVideoStore.getState();
        const cue = state.previewCue;
        if (cue && state.video?.v !== 2) {
          useVideoStore.setState({ currentTimeMs: cue.startMs + local, previewLocalMs: local });
          return;
        }
        useVideoStore.setState({ currentTimeMs: local, previewLocalMs: null });
      },
      onStateChange: (playback) => useVideoStore.setState({ playback }),
    });
  }
  return clock;
}

function currentProject(): Project | null {
  return useProjectStore.getState().currentProject;
}

/** Cue of the page currently open in the editor. */
function activeCue(): SceneCue | null {
  const activeId = currentProject()?.activePageId;
  const { cues } = useVideoStore.getState();
  return cues.find((c) => c.pageId === activeId) ?? cues[0] ?? null;
}

/** Point the clock at a cue (scene-local duration). */
function scopeTo(cue: SceneCue): PlaybackClock {
  const c = getClock();
  useVideoStore.setState({ previewCue: cue });
  c.setDuration(cue.endMs - cue.startMs);
  return c;
}

/** Write a VideoProject to the project store and refresh derived state. */
function writeVideo(next: VideoProject): VideoProject | null {
  const project = currentProject();
  if (!project) return null;
  const synced = next.v === 2 || !project.pages?.length ? next : syncScenesToPages(next, project.pages);
  const stamped: VideoProject = { ...synced, updatedAt: Date.now() };
  const cues = stamped.v === 2 ? [] : buildCues(stamped.scenes);
  // A duration edit while paused mid-preview must rescope the clock, not leave it stale.
  const prevCue = useVideoStore.getState().previewCue;
  const previewCue = prevCue ? cues.find((c) => c.pageId === prevCue.pageId) ?? null : null;
  useVideoStore.setState({ video: stamped, cues, previewCue });
  useProjectStore.getState().updateProject({ ...project, video: stamped });
  useCanvasStore.getState().markDirty(true);
  if (previewCue) clock?.setDuration(previewCue.endMs - previewCue.startMs);
  else if (prevCue) useVideoStore.getState().stop();
  return stamped;
}

function pushPast(past: VideoProject[], item: VideoProject): VideoProject[] {
  const next = [...past, item];
  return next.length > TIMELINE_HISTORY_LIMIT ? next.slice(-TIMELINE_HISTORY_LIMIT) : next;
}

const initialTimeline: TimelineState = {
  playback: 'stopped',
  currentTimeMs: 0,
  pxPerSec: DEFAULT_PX_PER_SEC,
  selectedSceneId: null,
  selectedLayerUid: null,
  loop: false,
  muted: false,
  cues: [],
};

export const useVideoStore = create<VideoStore>()(
  devtools(
    (set, get) => {
      const mutate = (
        fn: (video: VideoProject) => VideoProject,
        opts: MutateOptions = {}
      ): void => {
        const { video } = get();
        if (!video) return;
        const next = fn(video);
        if (next === video) return;
        if (opts.record !== false) set({ past: pushPast(get().past, video), future: [] });
        writeVideo(next);
      };

      return {
        ...initialTimeline,
        projectId: null,
        video: null,
        past: [],
        future: [],
        notice: null,

        setNotice: (notice) => set({ notice }),

        enableMotion: () => {
          const project = currentProject();
          if (!project) return;
          const pages = project.pages || [];
          const size = { width: project.width, height: project.height };
          if (project.kind === 'movie') {
            const existing = get().video;
            const base = existing ? migrateVideoProject(existing, size) : createClipProject(size, Date.now());
            writeVideo({ ...base, enabled: true });
            return;
          }
          const base =
            get().video ??
            createVideoProject(
              pages,
              size,
              Date.now()
            );
          writeVideo({ ...base, enabled: true });
        },

        disableMotion: () => {
          const { video } = get();
          if (!video) return;
          get().stop();
          writeVideo({ ...video, enabled: false });
        },

        syncToPages: (pages) => {
          const { video } = get();
          if (!video || !pages.length) return;
          const next = syncScenesToPages(video, pages);
          if (next !== video) writeVideo(next);
        },

        setSceneDuration: (pageId, ms, opts) => {
          const { video } = get();
          if (video) {
            const reason = explainDurationClamp(video, pageId, ms);
            if (reason) set({ notice: reason });
          }
          mutate((v) => setSceneDurationPure(v, pageId, ms), opts);
        },

        setSceneThumb: (pageId, atMs) => mutate((v) => setSceneThumbAt(v, pageId, atMs)),

        applyMotionTemplate: (templateId) => {
          const template = motionTemplateById(templateId);
          if (!template) return;
          mutate((v) => applyMotionTemplate(v, template));
        },

        setTransition: (pageId, transition, opts) =>
          mutate((v) => setTransitionPure(v, pageId, transition), opts),

        checkpoint: () => {
          const { video } = get();
          if (video) set({ past: pushPast(get().past, video), future: [] });
        },

        undo: () => {
          const { past, video } = get();
          if (!past.length || !video) return;
          const prev = past[past.length - 1];
          set({ past: past.slice(0, -1), future: [video, ...get().future] });
          writeVideo(prev);
        },

        redo: () => {
          const { future, video } = get();
          if (!future.length || !video) return;
          const [next, ...rest] = future;
          set({ future: rest, past: pushPast(get().past, video) });
          writeVideo(next);
        },

        previewCue: null,
        previewLocalMs: null,

        play: () => {
          const { video, playback, currentTimeMs, previewCue } = get();
          if (!video?.enabled) return;
          if (video.v === 2) {
            const c = getClock();
            set({ previewCue: null, previewLocalMs: null });
            c.setDuration(Math.max(video.totalDurationMs, 1));
            if (playback !== 'paused') c.seek(Math.min(currentTimeMs, video.totalDurationMs));
            c.play();
            return;
          }
          const cue = activeCue();
          if (!cue) return;
          const resume = playback === 'paused' && previewCue?.pageId === cue.pageId;
          const c = scopeTo(cue);
          if (!resume) {
            const local = currentTimeMs - cue.startMs;
            c.seek(local > 0 && local < cue.endMs - cue.startMs ? local : 0);
          }
          c.play();
        },
        pause: () => clock?.pause(),
        togglePlay: () => {
          if (get().playback === 'playing') get().pause();
          else get().play();
        },
        stop: () => {
          const cue = get().previewCue;
          clock?.stop();
          set({
            playback: 'stopped',
            previewCue: null,
            previewLocalMs: null,
            currentTimeMs: cue ? cue.startMs : get().currentTimeMs,
          });
        },
        seek: (ms) => {
          const video = get().video;
          if (video?.v === 2) {
            const c = getClock();
            const next = Math.max(0, Math.min(ms, video.totalDurationMs));
            set({ previewCue: null, previewLocalMs: null });
            c.setDuration(Math.max(video.totalDurationMs, 1));
            c.seek(next);
            return;
          }
          const cue = activeCue();
          if (get().video?.enabled && cue && ms >= cue.startMs && ms <= cue.endMs) {
            const c = scopeTo(cue);
            if (get().playback === 'playing') c.pause();
            c.seek(ms - cue.startMs);
            return;
          }
          get().cueTo(ms);
        },
        cueTo: (ms) => {
          get().stop();
          set({ currentTimeMs: Math.max(0, ms) });
        },
        setLoop: (loop) => {
          clock?.setLoop(loop);
          set({ loop });
        },
        setPxPerSec: (pxPerSec) =>
          set({ pxPerSec: Math.min(MAX_PX_PER_SEC, Math.max(MIN_PX_PER_SEC, pxPerSec)) }),
        selectScene: (selectedSceneId) => set({ selectedSceneId }),

        setAudio: (track) =>
          mutate((v) => ({ ...v, audio: track ? clampAudioTrack(track) : null })),
        updateAudio: (patch, opts) =>
          mutate((v) => (v.audio ? { ...v, audio: clampAudioTrack({ ...v.audio, ...patch }) } : v), opts),
        setMuted: (muted) => set({ muted }),
        selectedClipId: null,
        selectClip: (selectedClipId) => set({ selectedClipId }),
        changeTimeline: (fn, opts) => mutate(fn, opts),

        setExportSettings: (exportSettings) => {
          const { video } = get();
          if (!video || JSON.stringify(video.exportSettings) === JSON.stringify(exportSettings)) return;
          writeVideo({ ...video, exportSettings });
        },
        exportController: null,
        setExportController: (exportController) => set({ exportController }),
      };
    },
    { name: 'VideoStore' }
  )
);

/** Reset the mirror for a newly opened (or closed) project. */
function loadProject(project: Project | null): void {
  clock?.destroy();
  clock = null;
  useVideoStore.getState().exportController?.abort();
  useVideoStore.setState({
    ...initialTimeline,
    projectId: project?.projectId ?? null,
    video: project?.video ?? null,
    cues: project?.video && project.video.v !== 2 ? buildCues(project.video.scenes) : [],
    selectedClipId: null,
    past: [],
    future: [],
    notice: null,
    previewCue: null,
    previewLocalMs: null,
    exportController: null,
  });
}

/**
 * Keep the mirror attached to the open project. Also self-heals one race:
 * autosave's saveProject() commits a project snapshot taken before its
 * Firestore await, which can briefly replace a newer `video` written during
 * that await. When the project's copy is older than ours, write ours back.
 */
useProjectStore.subscribe((state) => {
  const project = state.currentProject;
  const mirror = useVideoStore.getState();
  if (!project) {
    if (mirror.projectId) loadProject(null);
    return;
  }
  if (project.projectId !== mirror.projectId) {
    loadProject(project);
    return;
  }
  const mine = mirror.video;
  const theirs = project.video;
  if (mine && (!theirs || theirs.updatedAt < mine.updatedAt)) {
    queueMicrotask(() => {
      const latest = currentProject();
      const ours = useVideoStore.getState().video;
      if (!latest || !ours || latest.projectId !== project.projectId) return;
      if (latest.video && latest.video.updatedAt >= ours.updatedAt) return;
      useProjectStore.getState().updateProject({ ...latest, video: ours });
      useCanvasStore.getState().markDirty(true);
    });
  }
});

// The editor may set currentProject before this module is first imported.
loadProject(useProjectStore.getState().currentProject);
